import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { VideoFeatures } from '@/store/videoFeatures/types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PitchRetentionCell } from '@/components/metrics/pitch/PitchRetentionCell';
import { BarChart3, Star, Info, Loader2 } from 'lucide-react';
import { AutoplayTestMetricsResponse } from '@/services/autoplayTestService';
import type { TestPitchMetric } from '@/types/pitch';
import { PitchSelector } from '@/components/metrics/pitch/PitchSelector';
import { byPitchOptions, usePitchSelection } from '@/components/metrics/pitch/pitchSelection';

interface AutoplayMetricsOverviewProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  metricsData?: AutoplayTestMetricsResponse;
  isLoading?: boolean;
  /** Refaz o fetch das métricas após configurar o pitch. */
  onPitchSaved?: () => void;
}

interface AutoplayVariationMetric {
  variationName: string;
  visualizacoes: number;
  visualizacoesUnicas: number;
  plays: number;
  playsUnicos: number;
  playRate: number;
  cliquesNoBotao: number;
  /** Pitches do vídeo ORIGINAL com a retenção desta variação em cada um. */
  pitches: TestPitchMetric[];
  pitchVideoId: string | null;
  videoDuration: number | null;
  engajamento: number;
  isChampion: boolean;
}

const TableHeadWithTooltip = ({ children, tooltip }: { children: React.ReactNode; tooltip: string }) => { useLanguage(); return ((
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger className="flex items-center gap-1 cursor-help">
        {children}
        <Info className="h-3 w-3 text-[#9CA3AF]" />
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-xs text-xs">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)); };

export const AutoplayMetricsOverview: React.FC<AutoplayMetricsOverviewProps> = ({
  features,
  metricsData,
  isLoading,
  onPitchSaved
}) => {
  useLanguage();

  const autoplayOverlay = features.autoplayOverlay || {
    enabled: false,
    abTestConfig: {
      enabled: false,
      variations: []
    }
  };

  const isTestEnabled = autoplayOverlay.abTestConfig?.enabled;
  const variations = autoplayOverlay.abTestConfig?.variations || [];

  // Converter métricas do backend para o formato esperado
  const metrics: AutoplayVariationMetric[] = useMemo(() => {
    // Debug: verificar o que o backend está retornando
    console.log('📊 Dados recebidos do backend:', metricsData);

    // Se temos dados reais do backend, usar eles
    if (metricsData && metricsData.data && Array.isArray(metricsData.data) && metricsData.data.length > 0) {
      const sortedMetrics = metricsData.data.map((item) => {
        const totais = item.metric.totais;

        const playRate = Number(totais.taxareproducao) || 0;
        const engajamento = Number(totais.engajamento) || 0;

        return {
          variationName: item.autoplayTest.nomeVideo,
          visualizacoes: Number(totais.visualizacoes) || 0,
          visualizacoesUnicas: Number(totais.visualizacoesUnicas) || 0,
          plays: Number(totais.plays) || 0,
          playsUnicos: Number(totais.playsUnicos) || 0,
          playRate: isFinite(playRate) ? playRate : 0,
          cliquesNoBotao: Number(totais.clicks) || 0,
          // Raiz do retorno de MetricService.execute. Os pitches vêm do vídeo
          // ORIGINAL (o clone não herda o campo) — o backend resolve isso via
          // pitchesOverride, e pitchVideoId aponta para o original.
          pitches: item.metric?.pitches ?? [],
          pitchVideoId: item.metric?.pitchVideoId ?? null,
          videoDuration: item.metric?.videoDuration ?? null,
          engajamento: isFinite(engajamento) ? engajamento : 0,
          isChampion: false // Será calculado depois
        };
      });

      // Ordenar por play rate (ou outra métrica relevante)
      sortedMetrics.sort((a, b) => b.playRate - a.playRate);

      // Marcar o primeiro como campeão
      if (sortedMetrics.length > 0) {
        sortedMetrics[0].isChampion = true;
      }

      return sortedMetrics;
    }

    // Se metricsData existe mas data não é um array
    if (metricsData && metricsData.data && !Array.isArray(metricsData.data)) {
      console.error('❌ metricsData.data não é um array:', typeof metricsData.data, metricsData.data);
    }

    // Se não há dados do backend mas o teste está habilitado, mostrar vazio
    if (isTestEnabled) {
      return [];
    }

    // Retornar array vazio se não houver dados
    return [];
  }, [metricsData, isTestEnabled]);

  // Seletor de pitch (só aparece com 2+ pitches). As variações compartilham os
  // pitches do vídeo original, então as opções saem da primeira linha.
  const pitchOptions = byPitchOptions(metrics[0]?.pitches ?? []);
  const [pitchId, setPitchId] = usePitchSelection(pitchOptions);

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="mx-auto h-12 w-12 text-muted-foreground mb-4 animate-spin" />
        <h3 className="text-lg font-medium mb-2">{translateInterfaceText("Carregando métricas...")}</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {translateInterfaceText("Aguarde enquanto buscamos os dados do teste")}</p>
      </div>
    );
  }

  // Verificar se há métricas para exibir
  const hasMetrics = metrics.length > 0;

  if (!hasMetrics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">
          {isTestEnabled ? translateInterfaceText("Aguardando dados") : translateInterfaceText("Nenhum teste ativo")}
        </h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          {isTestEnabled
            ? translateInterfaceText("📊 O teste está ativo, mas ainda não há métricas disponíveis. As métricas aparecerão assim que houver visualizações.")
            : translateInterfaceText("💡 Dica: Configure um teste na aba \"Teste A/B\" para visualizar métricas comparativas por variação.")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabela Detalhada */}
      <Card className="border-[#E6E6E6] rounded-xl shadow-[0px_2px_6px_rgba(0,0,0,0.06)]">
        <CardHeader className="bg-[#F7F9FC] border-b border-[#E6E6E6] py-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm font-semibold text-[#1C1C1E]">
              {translateInterfaceText("Comparativo por Variação")}</CardTitle>
            <PitchSelector options={pitchOptions} value={pitchId} onChange={setPitchId} />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F7F9FC] hover:bg-[#F7F9FC]">
                  <TableHead className="text-xs font-semibold text-[#6F6F75] whitespace-nowrap">{translateInterfaceText("Variação")}</TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual médio de retenção do vídeo assistido")}>
                      {translateInterfaceText("Engajamento")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Total de visualizações do vídeo")}>
                      {translateInterfaceText("Visualizações")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Visualizações únicas (por usuário)")}>
                      {translateInterfaceText("Vis. Únicas")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">{translateInterfaceText("Plays")}</TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Plays únicos por usuário")}>
                      {translateInterfaceText("Plays Únicos")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual de visitantes que iniciaram o vídeo")}>
                      {translateInterfaceText("Play Rate")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Percentual de espectadores que ainda assistia no momento do pitch")}>
                      {translateInterfaceText("Retenção no Pitch")}</TableHeadWithTooltip>
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-[#6F6F75] text-right whitespace-nowrap">
                    <TableHeadWithTooltip tooltip={translateInterfaceText("Número de cliques no botão")}>
                      {translateInterfaceText("C. no Botão")}</TableHeadWithTooltip>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.map((metric, index) => (
                  <TableRow 
                    key={index}
                    className={cn(
                      metric.isChampion 
                        ? 'bg-[#F0F7FF] border-l-2 border-l-[#1F6BFF] hover:bg-[#E8F4FF]' 
                        : index % 2 === 0 ? 'bg-white hover:bg-[#F9FAFB]' : 'bg-[#FAFBFC] hover:bg-[#F9FAFB]'
                    )}
                  >
                    <TableCell className="font-medium text-[#1C1C1E]">
                      <div className="flex items-center gap-2">
                        <span>{metric.variationName}</span>
                        {metric.isChampion && (
                          <Star className="h-3 w-3 text-[#1F6BFF] fill-[#1F6BFF]" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">
                      {metric.engajamento.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">{metric.visualizacoes}</TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">{metric.visualizacoesUnicas}</TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">{metric.plays}</TableCell>
                    <TableCell className="text-right text-[#1C1C1E]">{metric.playsUnicos}</TableCell>
                    <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                      {metric.playRate.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                      <PitchRetentionCell
                        pitch={metric.pitches.find((p) => p.id === pitchId) ?? null}
                        videoPitches={metric.pitches}
                        pitchVideoId={metric.pitchVideoId}
                        durationInSeconds={metric.videoDuration}
                        onSaved={onPitchSaved}
                      />
                    </TableCell>
                    <TableCell className="text-right text-[#1C1C1E] whitespace-nowrap">
                      {metric.cliquesNoBotao}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
